import * as React from "react";
import * as S from "@styled";
import { connect } from "react-redux";
import { HashMap } from "../../../shared/model/HashMap";
import { DispatchType } from "../../../shared/utilities";
import { addManualTriggerKey, removeManualTriggerKey, reorderManualTriggerParams, updateManualTriggerDescription, updateManualTriggerKey, updateManualTriggerType, useManualTriggerForMonitoringRuleChange as useProcessForMonitoringRuleChange } from "../../actions/ManualTriggerActions";
import { IManualTriggerParameter } from "../../model/IProcessData";
import { ManualTriggerConfig } from "../../model/ITriggerData";
import { State } from "../../model/State";
import { InputMode } from "./TriggerInput";
import { TypedTriggerInput } from "./TypedTriggerInput";
import LabelWithTooltip from "shared/components/LabelWithTooltip";
import CustomToggle from "shared/components/CustomToggle";

import { SortableContainer, SortableElement, SortableHandle } from "react-sortable-hoc";
import styled from "styled-components";
import colors from "shared/styles/colors";
import theme from "styled-theming";
import { ThemeType } from "shared/styles/theme";

const TriggerInput = TypedTriggerInput as new () => TypedTriggerInput<ManualTriggerConfig>;

const dragHandleColor = theme("mode", {
    [ThemeType.light]: colors.purplePrimary,
    [ThemeType.dark]: colors.purpleTertiary
})

const DragHandleContainer = styled(S.FlexContainer)`
    color: ${dragHandleColor};
    font-size: 0.938rem;
    margin-bottom: 15px;
`

const DragHandle = SortableHandle(() => {
    return <span style={{ cursor: "row-resize" }}><i className="ci-hamburger" ></i> Change order</span>;
});

const SortableTriggerParam = SortableElement(({ ...props }: { index: number, children: React.ReactNode }) => {
    return <S.FormGroup key={props.index}>{props.children}</S.FormGroup>;
});

const SortableTriggerParamContainer = SortableContainer(({ ...props }: { children: React.ReactNode }) => {
    return <div>{props.children}</div>;
});

export const ManualTrigger: React.FC<IManualTriggerProps> = (props) => {

    const reservervedWords = ["abstract", "as", "base", "bool", "break", "byte", "case", "catch", "char", "checked", "class", "const", "continue", "decimal", "default", "delegate", "do", "double", "else", "enum", "event", "explicit", "extern", "false", "finally", "fixed", "float", "for", "foreach", "goto", "if", "implicit", "in", "int", "interface", "internal", "is", "lock", "long", "namespace", "new", "null", "object", "operator", "out", "override", "params", "private", "protected", "public", "readonly", "ref", "return", "sbyte", "sealed", "short", "sizeof", "stackalloc", "static", "string", "struct", "switch", "this", "throw", "true", "try", "typeof", "uint", "ulong", "unchecked", "unsafe", "ushort", "using", "virtual", "void", "volatile", "while"];

    function update(newKey: string, key: string, index: number) {
        if (props.onKeyUpdate) {
            props.onKeyUpdate(index, newKey, "", false);
        }
        props.manualTrigger?.map((value, i) => {
            validateKeyAndUpdate(value, i);
        });
    }

    function validateKeyAndUpdate(parameter: IManualTriggerParameter, index: number) {
        const pattern = /^[a-zA-ZöäåüÖÄÅÜ][a-zA-Z0-9_öäåüÖÄÅÜ]*$/;
        let isInvalid = false;
        let errorString = "";
        switch (true) {
            case parameter.name === "":
                isInvalid = true;
                errorString = FRENDS.Localizations.Validation_KeyRequired;
                break;
            case !pattern.test(parameter.name):
                isInvalid = true;
                errorString = FRENDS.Localizations.EnvironmentVariables_KeyPattern;
                break;
            case reservervedWords.some((word) => word === parameter.name):
                isInvalid = true;
                errorString = FRENDS.Localizations.Validation_RestrictedWord;
                break;
            case (props.manualTrigger !== undefined && props.manualTrigger.filter((param) => param.name.toLowerCase() === parameter.name.toLowerCase()).length > 1):
                isInvalid = true;
                errorString = FRENDS.Localizations.Validation_KeyNameExists;
                break;
        }
        if (isInvalid || (parameter.isInvalid && parameter.invalidString === FRENDS.Localizations.Validation_KeyNameExists)) {
            if (props.onKeyUpdate) {
                props.onKeyUpdate(index, parameter.name, errorString, isInvalid);
            }
        }
    }

    const manualTriggerDefaultValues = props.manualTrigger?.map((param, idx) => ({
        key: `manualTriggerDefaultValue-${idx}`, val: param.nonStringifiedDefaultValue || ""
    }));
    let manualTriggersToMap: HashMap<string> = {};
    if (manualTriggerDefaultValues !== undefined) {
        // Convert manualTriggerDefaultValues to a HashMap for TriggerInput
        manualTriggersToMap = manualTriggerDefaultValues?.reduce((map: HashMap<string>, obj) => (map[obj.key] = obj.val, map), {});
    }

    const hasParams = () => (props.manualTrigger?.length ?? 0) > 0;
    const onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number, newIndex: number }) => {
        props.onArrayItemIndexChange && props.onArrayItemIndexChange(oldIndex, newIndex);
    }

    return (
        <div>
            {!props.isSubprocess && (
                <S.FormGroup id="use-for-monitoring-rule" $inline={true}>
                    <LabelWithTooltip
                        label="Use for Monitoring Rules"
                        description="When enabled, this Process can be triggered from a Monitoring Rule when it alerts."
                    />

                    <CustomToggle
                        checked={!!props.isProcessForMonitoringRule}
                        onChange={props.onUseForMonitoringRuleChange as any}
                        disabled={props.disabled}
                    />
                </S.FormGroup>
            )}
            {!props.isProcessForMonitoringRule && hasParams() && (
                <SortableTriggerParamContainer useDragHandle={true} onSortEnd={onSortEnd}>
                    {props.manualTrigger!.map((parameter, index) =>
                        <SortableTriggerParam key={index} index={index}>
                            <S.FormGroup variant={parameter.isInvalid ? S.FormGroupVariant.error : S.FormGroupVariant.primary}>
                                <DragHandleContainer justifySpaceBetween={true}>
                                    <DragHandle />
                                    <i className="ci-trash_full pointer" key={index + "remove"} onClick={() => !props.disabled && props.onKeyRemove && props.onKeyRemove(index)} title={FRENDS.Localizations.Remove} />
                                </DragHandleContainer>
                                <S.FlexContainer justifySpaceBetween={true} centerItems={true}>
                                    <S.Label>
                                        {FRENDS.Localizations.Key}
                                    </S.Label>
                                </S.FlexContainer>
                                <S.Input
                                    isFullWidth={true}
                                    type="text"
                                    key={index + "key"}
                                    value={parameter.name}
                                    disabled={props.disabled}
                                    onChange={(event: any) => update(event.target.value, parameter.name, index)}
                                />
                                {parameter.isInvalid ? <div className="error">{parameter.invalidString}</div> : null}
                            </S.FormGroup>

                            <S.FormGroup>
                                <TriggerInput
                                    key={index}
                                    parameters={manualTriggersToMap}
                                    parameterName={`manualTriggerDefaultValue-${index}`}
                                    label={FRENDS.Localizations.DefaultValue}
                                    inputMode={InputMode.text}
                                    elementId={props.id}
                                />
                            </S.FormGroup>

                            <S.FormGroup>
                                <S.Label>
                                    {FRENDS.Localizations.Description}
                                </S.Label>
                                <S.Input
                                    isFullWidth={true}
                                    key={index + "description"}
                                    type="text"
                                    disabled={props.disabled}
                                    onChange={(event: any) => props.onDescriptionChange && props.onDescriptionChange(index, event.target.value)}
                                    value={parameter.description}
                                />
                            </S.FormGroup>

                            <S.FormGroup $inline={true}>
                                <S.Label>
                                    {FRENDS.Localizations.MakeSecret}
                                </S.Label>
                                <CustomToggle
                                    key={index + "switch"}
                                    checked={parameter.isSecret}
                                    onChange={(checked: boolean, event: any, id: string) => props.onTypeChange && props.onTypeChange(index, checked)}
                                    disabled={props.disabled}
                                />
                            </S.FormGroup>

                        </SortableTriggerParam>)}
                </SortableTriggerParamContainer>
            )}
            {!props.isProcessForMonitoringRule && (
                <S.FormGroup id="add-manual-trigger-parameters">
                    {!props.manualTrigger?.some((parameter) => parameter.isInvalid) ? (
                        <S.Button
                            onClick={() => props.onAddKey && props.onAddKey()}
                            title={FRENDS.Localizations.AddParameter}
                            disabled={props.disabled}
                        >
                            {FRENDS.Localizations.AddParameter}
                        </S.Button>
                    ) : null}
                </S.FormGroup>
            )}
        </div>
    );
};

interface IManualTriggerOwnProps {
    id: string;
    existingParameters: ManualTriggerConfig;
}

interface IManualTriggerStateProps {
    manualTrigger?: IManualTriggerParameter[];
    disabled?: boolean;
    isProcessForMonitoringRule?: boolean;
    isSubprocess?: boolean;
    onKeyUpdate?: (index: number, newKey: string, validationError: string, isInvalid: boolean) => void;
    onTypeChange?: (index: number, checked: boolean) => void;
    onDescriptionChange?: (index: number, description: string) => void;
    onAddKey?: () => void;
    onKeyRemove?: (index: number) => void;
    onUseForMonitoringRuleChange?: (use: boolean) => void;
    onRefreshCompletionService?: (useForMonitoringRule: boolean) => void;
    onArrayItemIndexChange?: (oldIndex: number, newIndex: number) => void;
}

type IManualTriggerProps = IManualTriggerStateProps & IManualTriggerOwnProps;

const mapStateToProps = (state: State, ownProps: IManualTriggerStateProps): IManualTriggerStateProps => {
    return {
        manualTrigger: state.process.manualTrigger,
        disabled: state.uiState.disableEdit,
        isProcessForMonitoringRule: state.process.isForMonitoringRule,
        isSubprocess: state.process.isSubprocess
    };
};

const mapDispatchToProps = (dispatch: DispatchType<State>, ownProps: IManualTriggerStateProps): IManualTriggerStateProps => {
    return {
        onKeyUpdate: (index, newKey, validationError, isInvalid) => dispatch(updateManualTriggerKey(index, newKey, validationError, isInvalid)),
        onTypeChange: (index, checked) => dispatch(updateManualTriggerType(index, checked)),
        onDescriptionChange: (index, description) => dispatch(updateManualTriggerDescription(index, description)),
        onAddKey: () => dispatch(addManualTriggerKey()),
        onKeyRemove: (index) => dispatch(removeManualTriggerKey(index)),
        onUseForMonitoringRuleChange: (use) => dispatch(useProcessForMonitoringRuleChange(use)),
        onArrayItemIndexChange: (oldIndex, newIndex) => dispatch(reorderManualTriggerParams(oldIndex, newIndex))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ManualTrigger);
